import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ControlledModal from '../Modal/ControlledModal';
import { Button } from '../ui/button';
import { useBulkDeleteLibraryMutation } from '../../reactWrapper/Redux/apiSlice';
import { TOKEN } from '../../helper/constant';
import { useTypedSelector } from '../../reactWrapper/Redux/utilities';
import { LIBRARY } from './library.config';

const API = process.env.REACT_APP_BASE_API_URL;

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What one deletion drags along, as a short line under the chip. */
function Impact({ a }) {
  const bits = [];
  if (a.questions) bits.push(plural(a.questions, 'test question'));
  if (a.talents) bits.push(plural(a.talents, 'talent profile'));
  if (a.jobs) bits.push(plural(a.jobs, 'job'));
  if (a.roles?.length) bits.push(`under ${a.roles.join(', ')}`);
  else if (a.generic) bits.push('generic');
  return (
    <div className='mt-1 text-xs text-muted-foreground'>
      will be deleted{a.name !== a.value ? ` (matches "${a.name}")` : ''}{bits.length ? ` · ${bits.join(' · ')}` : ''}
    </div>
  );
}

/** The warning shown before anything is removed, with the totals spelled out. */
function ConfirmPanel({ s, label, labels, busy, onCancel, onConfirm }) {
  const listing = [s.talents ? plural(s.talents, 'talent profile') : null, s.jobs ? plural(s.jobs, 'job post') : null].filter(Boolean).join(' and ');
  return (
    /* Lives inside the modal on purpose: the popup's overlay would sit above
       a portalled dialog. */
    <div role='alertdialog' aria-labelledby='bulk-delete-confirm-title' className='border-t border-red-200 bg-red-50 px-6 py-4'>
      <h3 id='bulk-delete-confirm-title' className='text-base font-bold text-red-800'>Delete {plural(s.delete, label)}?</h3>
      <div className='mt-2 space-y-1.5 text-sm text-red-900'>
        <p>This cannot be undone.</p>
        {s.questions ? (
          <p className='font-semibold'>
            {plural(s.questions, 'test question')} written for these {labels} will be deleted too. Talents can no longer be tested on them.
          </p>
        ) : (
          <p>No test questions are attached to these {labels}.</p>
        )}
        {listing ? (
          <p>{listing} list at least one of them. Those profiles and jobs are kept; the deleted {labels} are simply cleared from them.</p>
        ) : null}
        {s.roles ? <p>They are removed from {plural(s.roles, 'role')}.</p> : null}
      </div>
      <div className='mt-4 flex justify-end gap-2'>
        <Button type='button' variant='outline' onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button type='button' onClick={onConfirm} disabled={busy} className='bg-red-600 text-white hover:bg-red-700'>
          {busy ? 'Deleting…' : `Yes, delete ${plural(s.delete, label)}`}
        </Button>
      </div>
    </div>
  );
}

/**
 * Bulk delete. The sheet has one column of names; the kind (skills for
 * now) is fixed by the caller. Picking a file runs a dry run on the server
 * and the preview shows, per name: found and what its deletion drags along
 * (test questions, talent profiles, jobs, roles), not found (with a similar
 * existing name to pick instead), or already named earlier in the sheet.
 * Delete opens a confirmation that spells out the totals, applies exactly
 * the preview and closes; the toast says what went. Same as the upload
 * modal: a summary screen behind a "Done" button only repeated the preview.
 */
export default function BulkDeleteModal({ open, kind = 'skill', onClose }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [resolutions, setResolutions] = useState({}); // "row:kind:index" -> existing id
  const [bulk, { isLoading }] = useBulkDeleteLibraryMutation();
  const accessToken = useTypedSelector((s) => s.auth?.accessToken);
  const label = (LIBRARY[kind]?.label || kind).toLowerCase();
  const labels = (LIBRARY[kind]?.plural || `${kind}s`).toLowerCase();

  useEffect(() => {
    if (!open) return;
    setFile(null); setPreview(null); setError(''); setOnlyProblems(false); setConfirm(false); setResolutions({});
    if (inputRef.current) inputRef.current.value = '';
  }, [open]);

  const describe = (err) => err?.data?.error?.message || err?.data?.message || err?.error || 'Something went wrong. Please try again.';

  const runPreview = async (f, res) => {
    setError('');
    try {
      const out = await bulk({ file: f, kind, dryRun: true, resolutions: res }).unwrap();
      setPreview(out.data);
    } catch (err) {
      setPreview(null);
      setError(describe(err));
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f); setPreview(null); setResolutions({}); setConfirm(false);
    if (f) runPreview(f, {});
  };

  const toggleResolution = (key, id) => {
    const next = { ...resolutions };
    if (next[key]) delete next[key]; else next[key] = id;
    setResolutions(next);
    runPreview(file, next);
  };

  const doDelete = async () => {
    setError('');
    try {
      const out = await bulk({ file, kind, dryRun: false, resolutions }).unwrap();
      setConfirm(false);
      const ap = out.data.applied || {};
      toast.success(`Deleted ${plural(ap.deleted || 0, label)}${ap.questions ? ` and ${plural(ap.questions, 'test question')}` : ''}`);
      onClose?.({ deleted: true });
    } catch (err) {
      setConfirm(false);
      setError(describe(err));
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = accessToken || localStorage.getItem(TOKEN) || '';
      const res = await fetch(`${API}/api/v1/admin/library/delete-template?kind=${kind}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Template download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `delete-${labels}-template.xlsx`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const data = preview;
  const s = data?.summary;
  const hasProblem = (r) => r.errors.length || r.actions.some((a) => a.action === 'duplicate');
  const rows = data ? data.rows.filter((r) => !onlyProblems || hasProblem(r)) : [];

  return (
    <ControlledModal
      open={open}
      closeModal={() => !isLoading && onClose?.()}
      closeOnDocumentClick={!isLoading}
      contentStyle={{ width: 'min(820px, 97vw)', borderRadius: '12px', padding: 0, border: 'none', background: 'transparent' }}
    >
      <div className='flex max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-card'>
        <div className='flex items-center justify-between border-b border-border px-6 py-4'>
          <h2 className='text-lg font-bold text-brand-navy'>Bulk delete {labels}</h2>
          <button type='button' onClick={() => onClose?.()} className='text-sm text-muted-foreground hover:text-foreground' disabled={isLoading}>
            Close
          </button>
        </div>

        <div className='flex flex-col gap-4 overflow-y-auto px-6 py-5'>
          <p className='text-sm text-muted-foreground'>
            One {label} per row, or several in a cell separated by commas. Only the name is needed: a deleted {label} is removed from every
            role it belongs to, and cleared from the talent profiles and jobs that list it. Its test questions are deleted with it. Names are
            matched ignoring case, spaces and hyphens. Up to 2,000 rows.{' '}
            <button type='button' onClick={downloadTemplate} className='font-semibold text-brand-navy underline'>
              Download the delete template
            </button>
          </p>

          <div className='flex flex-wrap items-center gap-3'>
            <input
              ref={inputRef}
              id='bulk-delete-file'
              type='file'
              accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              onChange={onPick}
              disabled={isLoading}
              className='text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium'
            />
            {isLoading && !preview ? <span className='text-sm text-muted-foreground'>Checking the sheet…</span> : null}
          </div>

          {error ? (
            <p role='alert' className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>{error}</p>
          ) : null}

          {data ? (
            <>
              <div className='flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-surface px-4 py-3 text-sm'>
                <span className='font-semibold text-brand-navy'>Preview · {data.fileName}</span>
                <span>{plural(s.rows, 'row')}</span>
                <span className='text-red-700'>{plural(s.delete, label)} to delete</span>
                {s.missing ? <span className='text-amber-700'>{s.missing} not found</span> : null}
                {s.duplicate ? <span className='text-muted-foreground'>{s.duplicate} repeated</span> : null}
                <span className='text-muted-foreground'>|</span>
                <span>{plural(s.questions, 'test question')}</span>
                <span>{plural(s.talents, 'talent profile')}</span>
                <span>{plural(s.jobs, 'job')}</span>
                <span>{plural(s.roles, 'role link')}</span>
              </div>
              {data.warnings?.length ? (
                <ul className='list-disc pl-5 text-xs text-amber-700'>{data.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              ) : null}
              {s.missing || s.duplicate ? (
                <label className='flex items-center gap-2 text-sm'>
                  <input type='checkbox' checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} /> Show only rows that need attention
                </label>
              ) : null}

              <div className={`max-h-[48vh] overflow-auto rounded-md border border-border ${isLoading ? 'opacity-60' : ''}`}>
                <table className='min-w-full border-collapse text-left text-sm'>
                  <thead className='sticky top-0 z-10 bg-surface text-xs uppercase tracking-wide text-muted-foreground'>
                    <tr>
                      <th className='border-b border-border px-2 py-2 font-mono'>#</th>
                      <th className='border-b border-border px-3 py-2'>{label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.row} className={`border-t border-border align-top ${r.errors.length ? 'bg-red-50/70' : ''}`}>
                        <td className='px-2 py-2 font-mono text-xs text-muted-foreground'>{r.row}</td>
                        <td className='px-3 py-2'>
                          <div className='flex flex-col gap-1.5'>
                            {(r.values?.[kind] || []).map((value, index) => {
                              const a = r.actions.find((x) => x.index === index) || null;
                              const resKey = `${r.row}:${kind}:${index}`;
                              const chosen = !!resolutions[resKey];
                              const tone = a?.action === 'delete' ? 'border-red-200 bg-red-50 text-red-800'
                                : a?.action === 'duplicate' ? 'border-border bg-surface text-muted-foreground line-through'
                                : 'border-amber-200 bg-amber-50 text-amber-800';
                              return (
                                <div key={index}>
                                  <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone}`}>{value}</span>
                                  {a?.action === 'delete' ? <Impact a={a} /> : null}
                                  {a?.action === 'duplicate' ? <div className='mt-1 text-xs text-muted-foreground'>already listed on row {a.firstRow}; skipped</div> : null}
                                  {a?.action === 'missing' ? <div className='mt-1 text-xs text-amber-800'>not in the library; skipped</div> : null}
                                  {a?.suggestion ? (
                                    <label className='mt-1 flex items-start gap-1.5 text-xs text-amber-800'>
                                      <input type='checkbox' className='mt-0.5' checked={chosen} disabled={isLoading} onChange={() => toggleResolution(resKey, a.suggestion.id)} />
                                      <span>Did you mean "{a.suggestion.name}"? Tick to delete that one instead.</span>
                                    </label>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>

        {confirm && s ? (
          <ConfirmPanel s={s} label={label} labels={labels} busy={isLoading} onCancel={() => setConfirm(false)} onConfirm={doDelete} />
        ) : (
          <div className='flex items-center justify-between gap-2 border-t border-border px-6 py-4'>
            <span className='text-xs text-muted-foreground'>
              {preview ? 'Names that are not found are skipped. Nothing is deleted until you confirm.' : ''}
            </span>
            <Button type='button' onClick={() => setConfirm(true)} disabled={!preview || !preview.summary.delete || isLoading} className='bg-red-600 text-white hover:bg-red-700'>
              {isLoading && preview ? 'Working…' : preview ? `Delete ${plural(preview.summary.delete, label)}` : 'Delete'}
            </Button>
          </div>
        )}
      </div>
    </ControlledModal>
  );
}
